import { Entry as HARFormatEntry } from 'har-format';
import { decodeIfBase64 } from './Analysis/getPossibleBase64Decodings';
import { decodeBase64WherePossible, decodeBase64AndGZipWherePossible } from './contentOrBase64DecodedContent';
import { urlToURL } from './utils/domains';

export type Direction = 'sent' | 'received';

export interface ContentSourceBase {
  readonly content: string;
  decodedFromBase64?: true;
  readonly direction: Direction;
}

export interface ContentSourceUrlComponent extends ContentSourceBase {
  readonly location: 'url:host' | 'url:path' | 'url:query-string' | 'url:query-key';
  readonly positionWithinUrl: number;
  readonly direction: 'sent';
}

export interface ContentSourceUrlQueryValueComponent extends ContentSourceBase {
  readonly location: 'url:query';
  readonly positionWithinUrl: number;
  readonly queryKey: string;
  readonly direction: 'sent';
}
export interface ContentSourceReferer extends ContentSourceBase {
  location: 'referer';
  direction: 'sent';
}
export interface ContentSourceBodyFormDataKey extends ContentSourceBase {
  readonly location: 'body:form-key';
  readonly direction: 'sent';
}
export interface ContentSourceBodyFormDataValue extends ContentSourceBase {
  readonly location: 'body:form';
  readonly direction: 'sent';
  readonly key: string;
}
export interface ContentSourceBodySentRaw extends ContentSourceBase {
  readonly location: 'body:raw';
  readonly direction: 'sent';
  readonly bodyIndex: number;
}
export interface ContentSourceBodySentJson extends ContentSourceBase {
  readonly location: 'body:json' | 'body:json-key';
  readonly key: string;
  readonly direction: 'sent';
}
export interface ContentSourceCookieNameSent extends ContentSourceBase {
  readonly location: 'cookie-name';
  readonly direction: 'sent';
}
export interface ContentSourceCookieValueSent extends ContentSourceBase {
  readonly location: 'cookie';
  readonly direction: 'sent';
  readonly cookieName: string;
}
export interface ContentSourceHeaderNameSent extends ContentSourceBase {
  readonly location: 'header-name';
  readonly direction: 'sent';
}
export interface ContentSourceHeaderValueSent extends ContentSourceBase {
  readonly location: 'header';
  readonly direction: 'sent';
  readonly headerName: string;
}
export interface ContentSourceHeaderValueReceived extends ContentSourceBase {
  readonly location: 'header';
  readonly direction: 'received';
  readonly headerName: string;
}
export interface ContentSourceHeaderNameReceived extends ContentSourceBase {
  readonly location: 'header-name';
  readonly direction: 'received';
}
export interface ContentSourceBodyReceived extends ContentSourceBase {
  readonly location: 'body';
  readonly direction: 'received';
  readonly isBase64Encoded: boolean;
}
export interface ContentSourceWebSocketFrameSent extends ContentSourceBase {
  readonly location: 'frame';
  readonly direction: 'sent';
  readonly isBase64Encoded: boolean;
}
export interface ContentSourceWebSocketFrameReceived extends ContentSourceBase {
  readonly location: 'frame';
  readonly direction: 'received';
  readonly isBase64Encoded: boolean;
}
export type ContentSourceProvidedByHarFormat =
  ContentSourceUrlComponent |
  ContentSourceUrlQueryValueComponent |
  ContentSourceBodyFormDataKey |
  ContentSourceBodyFormDataValue |
  ContentSourceBodySentRaw |
  ContentSourceBodySentJson |
  ContentSourceCookieNameSent |
  ContentSourceCookieValueSent |
  ContentSourceHeaderNameSent |
  ContentSourceHeaderValueSent |
  ContentSourceHeaderValueReceived |
  ContentSourceHeaderNameReceived |
  ContentSourceBodyReceived |
  ContentSourceWebSocketFrameSent |
  ContentSourceWebSocketFrameReceived |
  never;

export type ContentSource = 
  ContentSourceProvidedByHarFormat |
  ContentSourceWebSocketFrameReceived |
  ContentSourceWebSocketFrameSent;
//  ContentSourceWebSocketFrameReceived | 

// HAR format does not support
//  ContentSourceWebSocketFrameSent |
//  ContentSourceWebSocketFrameReceived | 
export type ContentSourceHar = ContentSourceProvidedByHarFormat & {
  entry: HARFormatEntry;
}

export const urlToContentSourceBases = (
  urlObjectOrString?: URL | string, {
    urlHandling = 'query-components'
}: {
  urlHandling?: 'single-query' | 'query-components';
} = {}
) : (ContentSourceUrlComponent | ContentSourceUrlQueryValueComponent)[] => {
  const url = typeof urlObjectOrString === 'string' ? urlToURL(urlObjectOrString) : urlObjectOrString;
  if (url == null) return [];
  const result: (ContentSourceUrlComponent | ContentSourceUrlQueryValueComponent)[] = [];
  let positionWithinUrl = url.protocol.length + 2;
  result.push({ positionWithinUrl, content: url.host, location: 'url:host', direction: 'sent' });
  positionWithinUrl += url.origin.length;
  result.push({ positionWithinUrl, content: url.pathname, location: 'url:path', direction: 'sent' });
  // The hash is never sent to the server, so it's not a type of sent/received content
  // positionWithinUrl = url.href.length - url.hash.length
  // result.push({ positionWithinUrl, content: url.hash, location: 'url:hash', direction: 'sent' });
  if (urlHandling === 'single-query') {
    positionWithinUrl = url.href.length - (url.hash.length + url.search.length);
    result.push({ positionWithinUrl, content: url.search, location: 'url:query-string', direction: 'sent' });
  } else {
    const positionOfSearch = url.href.length - (url.hash.length + url.search.length);
    for (const [key, value] of url.searchParams) {
      positionWithinUrl = positionOfSearch + url.search.indexOf(`${key}=`);
      result.push({ positionWithinUrl, ...decodeBase64WherePossible(key), location: 'url:query-key', direction: 'sent' });
      positionWithinUrl += key.length + 1;
      result.push({ positionWithinUrl, ...decodeBase64WherePossible(value), location: 'url:query', direction: 'sent', queryKey: key });
    }
  }
  return result.filter( c => c.content.length > 0);
}

export type JSON = string | number | boolean | {[key: string]: JSON} | JSON[] | null;

export const bodyStringToContentSources = async (bodyString?: string): Promise<ContentSource[]> =>	{
	const toJsonContentSourcesRecursive = (recursiveKey: string, value: JSON): ContentSourceBodySentJson[] => {
		if (value == null) return [];
		if (Array.isArray(value)) {
      const results = [] as ContentSourceBodySentJson[];
      for (const [i, v] of value.entries()) {
        const key = `${recursiveKey}[${i}]`;
        const result = toJsonContentSourcesRecursive(key, v);
        results.push(...result);
      }
      return results;
			// return ([] as ContentSourceBodySentJson[]).concat(
			// 	...value.map( (v, i) => toJsonContentSourcesRecursive(`${recursiveKey}[${i}]`, v) )
			// );
		} else if (typeof value === "object") {
			return ([] as ContentSourceBodySentJson[]).concat(
				...Object.entries(value).map( ([key, v]) => ([
					...(recursiveKey.length < 3 ? [] : [{ direction: 'sent', location: 'body:json-key', key: recursiveKey, content: `${recursiveKey}.${key}` } satisfies ContentSourceBodySentJson]),
					...toJsonContentSourcesRecursive(`${recursiveKey}${recursiveKey.length === 0 ? '' : '.'}${key}`, v)
				]))
			)
		} else if (typeof value === "string" && value.length > 2) {
			return [{ direction: 'sent', location: 'body:json', key: recursiveKey, ...decodeBase64WherePossible(value) } satisfies ContentSourceBodySentJson];
		} else if (typeof value === "number" && !Number.isNaN(value) && Number.isFinite(value)) {
			return [{ direction: 'sent', location: 'body:json', key: recursiveKey, content: `${value}` } satisfies ContentSourceBodySentJson];
    }

		// We don't return numbers, boolean, or short strings as there's nothing worth searching
		return [];
	}
  if (bodyString == null) return [];
  const text = decodeIfBase64(bodyString);
  try {
    return toJsonContentSourcesRecursive("", JSON.parse(text) as JSON);
  } catch {
    const content = await decodeBase64AndGZipWherePossible(text);
    if (content != null) {
      return [{ direction: 'sent', location: 'body:raw', ...content, bodyIndex: -1 } satisfies ContentSourceBodySentRaw as ContentSourceBodySentRaw];
    }
    return [];
  }
}

export type ContentLocation = ContentSource['location'];

export const enhancedContentSourceLocation = (cs: Omit<ContentSource, 'content'>) => {
  const contentSource = cs as ContentSource;
  const received = contentSource.direction === 'received' ? 'received:' : '';
  const decodingOf: <T extends string>(s: T) => T | `base64Decoded(${T})` = cs.decodedFromBase64 ? ((s) => `base64Decoded(${s})`) : ((s) => s);
	switch (contentSource.location) {
		case "cookie":
			return decodingOf(`${received}${contentSource.location}:${contentSource.cookieName}` as const);
		case "header":
			return decodingOf(`${received}${contentSource.location}:${contentSource.headerName}` as const);
		case "url:query":
			return decodingOf(`${received}${contentSource.location}:${contentSource.queryKey}` as const);
    case "body:form":
      return decodingOf(`${contentSource.location}:${contentSource.key}` as const);
    case "body:json-key":
    case "body:json":
      return decodingOf(`${contentSource.location}:${contentSource.key}` as const);
	}
	return decodingOf(`${received}${contentSource.location}` as const);
}
export type EnhancedContentSourceLocation = ReturnType<typeof enhancedContentSourceLocation>;
export const inferDataTypeNameFromContentSource = (contentSource: ContentSource): string | undefined => {
	switch (contentSource.location) {
		case "url:query":
			return contentSource.queryKey;
		case "body:form":
		case "body:json":
			return contentSource.key;
		case "cookie":
			return contentSource.cookieName;
		default:
			return undefined;
	}
};
